<template>
  <v-container fluid>
    <v-card class="elevation-2 px-2 mb-4 rounded-lg">
      <v-row class="px-4 py-3">
        <v-col cols="10" sm="5" class="pb-0">
          <v-text-field
            dense
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            outlined
          ></v-text-field>
          <v-spacer></v-spacer>
        </v-col>
        <v-btn
          color="#3F51B5"
          class="my-3"
          dark
          depressed
          height='40'
          max-width='40'
          min-width='40'
          @click="cleanFilters"
        >
          <v-icon>
            mdi-autorenew
          </v-icon>
        </v-btn>
      </v-row>
    </v-card>
    <v-card class="elevation-2 pt-5 rounded-lg">
      <v-card-title>
        <v-row class="px-2 my-0">
          <v-spacer></v-spacer>
          <v-col cols="7" md="6" sm="9" class="d-flex justify-end pb-0 pt-0 mt-0">
            <vue-excel-xlsx
              :data="dataFilteredForExcel"
              :columns="columnsData"
              :filename="`Cruise_Campaigns-Settings`"
              :sheetname="`Settings`"
            >
              <v-btn
                height="39"           
                min-width="39px"        
                :disabled="loaded"
                class="ml-auto mr-5 rounded-lg pa-0 ma-0"                           
                color="white"
              >
                <ExportExcel />
              </v-btn> 
            </vue-excel-xlsx>
            <CustomColumns 
              :currentValue="value"
              :currentCustomHeader="customHeader"
              :currentHeaders="headers"
              :settingsName="settingsName"
              @getCustomData="getCustomData"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-hover v-model="hoverOnTable">
        <v-card-text>
          <div class="d-flex justify-space-between">        
            <v-btn
              class="mx-2 scroll-btn btn-left-arrow"
              fab
              small     
              @click="scrollingLeft"
            >
            <v-icon>
              mdi-arrow-left
            </v-icon>
            </v-btn>
            <v-btn
              class="mx-2 scroll-btn btn-right-arrow"
              fab
              small
              @click="scrollingRight"
            >
            <v-icon>
              mdi-arrow-right
            </v-icon>
            </v-btn>
          </div>
          <v-data-table
            fixed-header
            height="551px"
            class="elevation-0 table-main-container"
            :page.sync="pagination.page"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 75, 100, -1]
              }"
            :headers="selectedHeaders"
            :items="campaigns"
            item-key="campaignId"
            :search="search"
            :loading="loaded"
            loading-text="Loading... Please wait"
            @click:row="rowClick"
          >
            <template v-slot:item.actions="{ item }">
              <div class="d-flex flex-column">
                <v-btn x-small color="indigo white--text" @click="getDailyLog(item)">Daily Log</v-btn>
              </div>
            </template>
            <template v-slot:item.campaignName="{ item }">
              <v-hover v-slot:default="{ hover }">
                <td class="d-flex flex-column text-truncate">
                    <a @click="getCampaignSettings(item)" v-if="managementRight">
                      {{ item.campaignName }}
                      <v-icon :style="{'opacity': hover ? 1 : 0}" class="ml-2 " small color="blue darken-2">
                        mdi-pencil
                      </v-icon>
                    </a>
                  <span v-else>{{ item.campaignName }}</span>
                </td>
              </v-hover>            
            </template>
            <template v-slot:item.status="{ item }">
              <span v-if="item.hasPositionIssue" class="red--text">{{ item.status }}</span>
              <span v-else>{{ item.status }}</span>
            </template> 
          </v-data-table>
          <v-dialog v-model="dialogCampaignSettings" max-width="600px" persistent>
            <v-card>
              <v-card-title>
                <span class="headline">{{selectedCampaignName}} Settings</span>
              </v-card-title>
              <v-card-text class="pb-0">
                <v-container>
                  <v-row>
                    <v-col>
                      <v-text-field type="number" label="Sport First Ratio" v-model="settings.sportFirst"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field type="number" label="Max Sport First, $" v-model="settings.maxSportFirst"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field type="number" label="Sport Top Ratio" v-model="settings.sportTop"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field type="number" label="Max Sport Top, $" v-model="settings.maxSportTop"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field type="number" label="Sport First Page Ratio" v-model="settings.sportFirstPage"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field type="number" label="Max Sport First Page, $" v-model="settings.maxSportFirstPage"></v-text-field>
                    </v-col>
                  </v-row>
                  <hr style="border-top: 1px dashed lightgray;"/>
                  <v-row>
                    <v-col>
                      <v-switch v-model="settings.cruiseControl">
                        <template v-slot:label>
                          <div class="ml-1">Automated Cruise</div>
                        </template>
                      </v-switch>
                    </v-col>
                    <v-col>
                      <v-switch v-model="settings.isSilentModeMailing">
                        <template v-slot:label>
                          <div class="ml-1">Exclude from Mailing</div>
                        </template>
                      </v-switch>
                    </v-col>
                  </v-row>
                  <v-row  v-show="settings.cruiseControl">
                    <v-col>
                      <v-text-field type="number" label="Count Sport First" v-model="settings.countSportFirst"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field type="number" label="Count Sport Top" v-model="settings.countSportTop"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field type="number" label="Count Sport First Page" v-model="settings.countSportFirstPage"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row  v-show="settings.cruiseControl">
                    <v-col>
                      <v-text-field type="number" label="Frequency Apply Rank (in days)" v-model="settings.dayAppliedRank"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field disabled label="Last Applied Rank" v-model="settings.lastAppliedRank"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row
                    class="fill-height"
                    align-content="center"
                    justify="center"
                    v-if="settingsSaveLoaded"
                  >
                    <v-col cols="12">
                      <v-progress-linear
                        indeterminate
                        rounded
                        height="6"
                        :active="settingsSaveLoaded"
                      ></v-progress-linear>
                    </v-col>
                    <v-col
                      class="text-caption text-center"
                      cols="12"
                    >
                      Settings are saving... Please wait
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="cancelSettings" :disabled="settingsSaveLoaded">Cancel</v-btn>
                <v-btn color="blue darken-1" text @click="saveSettings" :disabled="settingsLoaded || settingsSaveLoaded">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="dialogDailyLog" persistent max-width="800px">
              <v-card>
                <v-card-title>
                  <span class="headline">Daily Log</span>
                </v-card-title>
                <v-card-text>
                  <RangeFilter
                    class="mb-7 mt-1"
                    :end="dateEndLog"
                    :start="dateStartLog"
                    @getDates="getDatesHistoryLog"
                    :excludePeriod="[]"
                    :defaultPeriod="4"
                    :setDates="false"
                  />
                  <v-container>
                    <p class="mt-4 text-center" v-if="dialogDailyLogLoading">Loading... Please wait</p>
                    <div v-if="!dialogDailyLogLoading" style="max-height: 500px; overflow: auto; font-size: 12px;">
                      <table border="1" v-for="d in dailyLog" :key="d">
                       <caption>{{ d.campaign_name }}</caption>
                       <tr>
                        <th>Date</th>
                        <th>First Mode</th>
                        <th>First Position</th>
                        <th>First Position From First Mode</th>
                        <th>Top Mode</th>
                        <th>Top Position</th>
                        <th>Top Position From Top Mode</th>
                        <th>First Page Mode</th>
                        <th>First Page Position</th>
                        <th>First Page Position From First Page Mode</th>
                       </tr>
                       <tr v-for="r in d.data" :key="r">
                         <td>{{r.hour}}</td>
                         <td>{{r.in_first_mode}}</td>
                         <td>{{r.in_first_position}}</td>
                         <td v-if="r.first_less" bgcolor="red" style="color:white">{{r.in_first_position_from_first_mode}}</td>
                         <td v-else>{{r.in_first_position_from_first_mode}}</td>
                         <td>{{r.in_top_mode}}</td>
                         <td>{{r.in_top_position}}</td>
                         <td v-if="r.top_less" bgcolor="red" style="color:white">{{r.in_top_position_from_top_mode}}</td>
                         <td v-else>{{r.in_top_position_from_top_mode}}</td>
                         <td>{{r.in_first_page_mode}}</td>
                         <td>{{r.in_first_page_position}}</td>
                         <td v-if="r.first_page_less" bgcolor="red" style="color:white">{{r.in_first_page_position_from_first_mode}}</td>
                         <td v-else>{{r.in_first_page_position_from_first_mode}}</td>
                       </tr>
                      </table>
                    </div>
                    <p class="mt-4 text-center" v-if="!dialogDailyLogLoading && dailyLog.length == 0">No data</p>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="dialogDailyLog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
        </v-card-text>
      </v-hover>
    </v-card>
  </v-container>
</template>

<script>
import axios from '@/api/axios-auth'
import CustomColumns from '@/components/customizing/CustomColumns'
import ExportExcel from '@/components/customizing/ExportExcel'
import RangeFilter from '@/components/filters/RangePicker'

export default {
  name: 'cruise-campaign',
  components: {
   CustomColumns,
   ExportExcel,
   RangeFilter
  },
  data() {
    return {
      campaigns: [],
      loaded: false,
      dialogDailyLog: false,
      dialogDailyLogLoading: false,
      dailyLog: [],
      search: '',
      headers: [
        {text: 'Campaign Name', value: 'campaignName', class: 'black--text'},
        {text: 'Status', value: 'status', class: 'black--text'},
        {text: 'Max Sport First', value: 'maxSportFirst', class: 'black--text'},
        {text: 'Max Sport Top', value: 'maxSportTop', class: 'black--text'},
        {text: 'Max Sport First Page', value: 'maxSportFirstPage', class: 'black--text'},
        {text: 'Sport First Ratio', value: 'sportFirst', class: 'black--text'},
        {text: 'Sport Top Ratio', value: 'sportTop', class: 'black--text'},
        {text: 'Sport First Page Ratio', value: 'sportFirstPage', class: 'black--text'},
        {text: 'Count Sport First', value: 'countSportFirst', class: 'black--text'},
        {text: 'Count Sport Top', value: 'countSportTop', class: 'black--text'},
        {text: 'Count Sport First Page', value: 'countSportFirstPage', class: 'black--text'},
        {text: 'Frequency Apply Rank (in days)', value: 'dayAppliedRank', class: 'black--text'},
        {text: 'Last Applied Rank', value: 'lastAppliedRank', class: 'black--text'},
        {text: 'Sport First > 50%', value: 'sportFirstMore50Pct', class: 'black--text'},
        {text: 'Sport Top > 50%', value: 'sportTopMore50Pct', class: 'black--text'},
        {text: '# of Sport First', value: 'numSportFirst', class: 'black--text'},
        {text: '# of Sport Top', value: 'numSportTop', class: 'black--text'},
        {text: '# of Sport First Page', value: 'numSportFirstPage', class: 'black--text'},
        {text: '# of Normal', value: 'numNormal', class: 'black--text'},
        {text: '# First Position', value: 'numFirstPosition', class: 'black--text'},
        {text: '# Top Position', value: 'numSportPosition', class: 'black--text'},
        {text: '# First Page Position', value: 'numFirstPagePosition', class: 'black--text'},
        {text: '# 0 First/Top CPC', value: 'numZeroFirstTopCpc', class: 'black--text'},
        {text: '# N/A', value: 'numNaPosition', class: 'black--text'},
        {text: 'Action', value: 'actions', class: 'black--text'},
      ],
      scrollLeft: 0,
      scrollWidth: 0,
      clientWidth: 0,
      hoverOnTable: false,
      dialogCampaignSettings: false,
      role: null,
      rights: [],
      settings: {
        sportFirst: 0,
        maxSportFirst: 0,
        sportTop: 0,
        maxSportTop: 0,
        countSportFirst: 0,
        countSportTop: 0,
        cruiseControl: false,
        dayAppliedRank: 0,
        lastAppliedRank: 0
      },
      dateEndLog: new Date().toISOString().substr(0, 10),
      dateStartLog: new Date(
          Date.UTC(
              new Date().getFullYear(),
              new Date().getMonth(),
              1)).toISOString().substr(0, 10),
      selectedCampaign: null,
      selectedCampaignName: null,
      settingsLoaded: false,
      settingsSaveLoaded: false,
      columnsData: [],
      settingsName: 'settings',
      value: [
        {text: 'Campaign Name', value: 'campaignName', class: 'black--text'},
        {text: 'Status', value: 'status', class: 'black--text'},
        {text: 'Max Sport First', value: 'maxSportFirst', class: 'black--text'},
        {text: 'Max Sport Top', value: 'maxSportTop', class: 'black--text'},
        {text: 'Max Sport First Page', value: 'maxSportFirstPage', class: 'black--text'},
        {text: 'Sport First Ratio', value: 'sportFirst', class: 'black--text'},
        {text: 'Sport Top Ratio', value: 'sportTop', class: 'black--text'},
        {text: 'Sport First Page Ratio', value: 'sportFirstPage', class: 'black--text'},
        {text: 'Count Sport First', value: 'countSportFirst', class: 'black--text'},
        {text: 'Count Sport Top', value: 'countSportTop', class: 'black--text'},
        {text: 'Count Sport First Page', value: 'countSportFirstPage', class: 'black--text'},
        {text: 'Frequency Apply Rank (in days)', value: 'dayAppliedRank', class: 'black--text'},
        {text: 'Last Applied Rank', value: 'lastAppliedRank', class: 'black--text'},
        {text: 'Sport First > 50%', value: 'sportFirstMore50Pct', class: 'black--text'},
        {text: 'Sport Top > 50%', value: 'sportTopMore50Pct', class: 'black--text'},
        {text: '# of Sport First', value: 'numSportFirst', class: 'black--text'},
        {text: '# of Sport Top', value: 'numSportTop', class: 'black--text'},
        {text: '# of Sport First Page', value: 'numSportFirstPage', class: 'black--text'},
        {text: '# of Normal', value: 'numNormal', class: 'black--text'},
        {text: '# First Position', value: 'numFirstPosition', class: 'black--text'},
        {text: '# Top Position', value: 'numSportPosition', class: 'black--text'},
        {text: '# First Page Position', value: 'numFirstPagePosition', class: 'black--text'},
        {text: '# 0 First/Top CPC', value: 'numZeroFirstTopCpc', class: 'black--text'},
        {text: '# N/A', value: 'numNaPosition', class: 'black--text'},
        {text: 'Action', value: 'actions', class: 'black--text'},
      ],
      customHeader: ['campaignName', 'status', 'maxSportFirst', 'maxSportTop', 'sportFirst', 'sportTop', 'countSportFirst',
                    'countSportTop', 'dayAppliedRank', 'lastAppliedRank', 'sportFirstMore50Pct', 'sportTopMore50Pct',
                    'numSportFirst', 'numSportTop', 'numNormal', 'numFirstPosition', 'numSportPosition', 'numZeroFirstTopCpc', 'numNaPosition', 'actions'],
      selectedHeaders: [],
      pagination: {
        page: 1
      }
    }
  },
  watch: {
    campaigns(){
      this.pagination.page = 1
    },
    hoverOnTable(){
      this.showScrollingArrows()
    },
    value(val) {
      this.selectedHeaders = val
      this.getHeaderForExcel()
    }
  },
  created() {
    this.selectedHeaders = this.value
    this.getHeaderForExcel()

    window.addEventListener("resize", this.showScrollingArrows)
  },
  destroyed() {
    window.removeEventListener("resize", this.showScrollingArrows)
  },
  mounted() {
    this.loaded = true
    
    axios
      .get('/users/me')
      .then(response => {
        this.role = response.data.data.role
        this.rights = response.data.data.rights
        
        this.getCampaigns(() => this.loaded = false)
      })

      let userName = localStorage.getItem('username')
      let settings = this.settingsName
      let clientId = (localStorage.getItem('ClientId')) ? localStorage.getItem('ClientId') : 1
      if (localStorage.getItem(`${userName}.${clientId}.${settings}`)) {
      
       try {

        let valueCash = JSON.parse(localStorage.getItem(`${userName}.${clientId}.${settings}`))
        this.value = valueCash.filter(e => this.headers.find(item => item.value === e.value))
        
        const parsed = JSON.stringify(this.value)
        localStorage.setItem(`${userName}.${clientId}.${settings}`, parsed)

        this.customHeader = this.value.map(e => e.value)

        this.getHeaderForExcel()
       } catch(e) {
         console.log("")
      }
    }

    this.showScrollingArrows()
  },
  computed: {
    managementRight(){
     return (this.role === "Admin" )
                               ? true
                               : this.rights.includes('cruiseCampaignManagement')
    },
    dataFilteredForExcel(){
      console.log('this.campaigns: ',this.campaigns)
      if (!this.campaigns) return []
      let data = JSON.parse(JSON.stringify(this.campaigns))
      console.log('dataFilteredForExcel: ', data)
      return data
    }
  },
  methods: {
    getDatesHistoryLog(dates) {
      this.dateStartLog = dates.dateStart
      this.dateEndLog = dates.dateEnd

      this.getDailyLog(this.selectedCampaign)
    },
    getCampaigns(callback){ 
      axios
        .get('/settings/campaigns')
        .then(response => {
          this.campaigns = response.data
          this.campaigns.map(e => {
            if (!e.isCruiseControl && !e.isSemiAutomated){
              e.status = 'Normal'
            } else {
              e.status = e.isCruiseControl ? 'Automated Cruise' : 'Semi-automated Cruise'
            }

            e.sportFirstMore50Pct = (e.sportFirstMore50Pct) ? 'Yes' : 'No'
            e.sportTopMore50Pct = (e.sportTopMore50Pct) ? 'Yes' : 'No'
            e.countSportFirst = (e.status == 'Automated Cruise') ? e.countSportFirst : 'N/A'
            e.countSportTop = (e.status == 'Automated Cruise') ? e.countSportTop : 'N/A'
          })
          this.dataFilteredForExcel
          if (callback) callback()
        })
    },
    rowClick(item, row){
      let selectState = (row.isSelected) ? false : true
      row.select(selectState)
    },
    cleanFilters(){
      this.search = ''
    },
    scrollingRight(){
      const el = document.querySelector('.v-data-table__wrapper')

      el.scrollLeft = el.scrollLeft + 50

      const btnRight = document.querySelector('.btn-right-arrow')
      const btnLeft = document.querySelector('.btn-left-arrow')
      btnRight.style.visibility = (this.hoverOnTable && (el.scrollLeft + el.clientWidth != el.scrollWidth)) ? 'visible' : 'hidden'
      btnLeft.style.visibility = this.hoverOnTable ? 'visible' : 'hidden'
    },
    scrollingLeft(){
      const el = document.querySelector('.v-data-table__wrapper')

      el.scrollLeft = el.scrollLeft - 50

      const btnLeft = document.querySelector('.btn-left-arrow')
      const btnRight = document.querySelector('.btn-right-arrow')
      btnLeft.style.visibility = (this.hoverOnTable && (el.scrollLeft != 0)) ? 'visible' : 'hidden'
      btnRight.style.visibility = this.hoverOnTable ? 'visible' : 'hidden'
    },
    showScrollingArrows(){
      const el = document.querySelector('.v-data-table__wrapper')
      this.scrollLeft = el.scrollLeft
      this.scrollWidth = el.scrollWidth
      this.clientWidth = el.clientWidth

      const btnRight = document.querySelector('.btn-right-arrow')
      const btnLeft = document.querySelector('.btn-left-arrow')
      btnRight.style.visibility = (this.hoverOnTable && (this.scrollLeft + this.clientWidth !== this.scrollWidth)) ? 'visible' : 'hidden'
      btnLeft.style.visibility = (this.hoverOnTable && (this.scrollLeft != 0)) ? 'visible' : 'hidden'
    },
    getDailyLog(item){
      this.dialogDailyLog = true
      this.dialogDailyLogLoading = true
      this.selectedCampaign = item

      axios
        .get(`/campaign-daily-log/${this.dateStartLog}/${this.dateEndLog}/${item.campaignId}`)
        .then(response => {
          this.dailyLog = response.data.data
          this.dialogDailyLogLoading = false
        })
    },
    getCampaignSettings(item){
      this.settingsLoaded = true
      this.selectedCampaign = item.campaignId
      this.selectedCampaignName = item.campaignName
      this.dialogCampaignSettings = true

      axios
        .get('/settings-campaign/' + this.selectedCampaign)
        .then(response => {
          this.settings = response.data
          this.settingsLoaded = false   
        })
    },
    saveSettings() {
      this.settingsSaveLoaded = true
      axios
        .post('/settings-campaign/' + this.selectedCampaign, this.settings)
        .then(() => {
            this.getCampaigns(() => {
              this.settingsSaveLoaded = false
              this.dialogCampaignSettings = false
              this.settings = {
                sportFirst: 0,
                maxSportFirst: 0,
                sportTop: 0,
                maxSportTop: 0,
                countSportFirst: 0,
                countSportTop: 0,
                cruiseControl: false,
                dayAppliedRank: 0,
                lastAppliedRank: 0
              }
            })
          }
        )
    },
    cancelSettings(){
      this.dialogCampaignSettings = false
      this.settings = {
        sportFirst: 0,
        maxSportFirst: 0,
        sportTop: 0,
        maxSportTop: 0,
        countSportFirst: 0,
        countSportTop: 0,
        cruiseControl: false,
        dayAppliedRank: 0,
        lastAppliedRank: 0
      }
    },
    getCustomData({customHeader, value, selectedHeaders}){
      this.customHeader = customHeader
      this.value = value
      this.selectedHeaders = selectedHeaders

      this.getHeaderForExcel()
    },
    getHeaderForExcel(){
      this.columnsData = this.selectedHeaders.map(e => {
        const obj = {}
          if (e.value !== "status") {
            obj.label = e.text
            obj.field = e.value
          }

          return obj
      }).filter(value => Object.keys(value).length !== 0)
      console.log('this.columnsData:', this.columnsData)
    },
    priceFormat(value){
      return '<p style="font-size:20px; color: red">'  + value + '</p>';
    }
  }
}
</script>

<style scoped>
  .btn-right-arrow {
    right: 0px;
    top: 80px !important;
  }
  .btn-left-arrow {
    left: 0px;
    top: 80px;
  }
</style>